import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { LogicielCaisseTicketService } from 'app/services/logiciel-caisse/logiciel-caisse-ticket.service';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

export type MultiSelectTicketKind =
  | 'available'
  | 'paid'
  | 'already_cancelled'
  | 'blocked'
  | 'already_invoiced'
  | 'not_ready'
  | 'ready'
  | 'consolide_blocked'
  | 'particulier_blocked'
  | 'cloture_blocked';

export type TicketClientType = 'particulier' | 'patente';

interface MultiSelectTicketClassification {
  kind: MultiSelectTicketKind;
  requiresAdmin: boolean;
  canCancel: boolean;
  canFacture: boolean;
  error?: string;
}

export interface MultiSelectTicketRow {
  id: number;
  numero: string;
  date: string;
  entrepot: string;
  entrepotId: number | null;
  entrepotCode: string;
  statut: string;
  reglement: string | null;
  clientName: string;
  clientId: number | null;
  clientType: TicketClientType;
  isConsolide: boolean;
  isCloture: boolean;
  /** sous_type document (acompte, reservation_avec_paiement, …). */
  sousType: string | null;
  total: number;
  subtotal: number;
  paymentModes: { libelle: string; montant: number }[];
  kind: MultiSelectTicketKind;
  requiresAdmin: boolean;
  canCancel: boolean;
  canFacture: boolean;
  loading: boolean;
  error?: string;
}

@Component({
  selector: 'app-detaille-multi-selection-ticket',
  templateUrl: './detaille-multi-selection-ticket.component.html',
  styleUrls: ['./detaille-multi-selection-ticket.component.scss'],
})
export class DetailleMultiSelectionTicketComponent implements OnInit, OnChanges {
  @Input() ticketIds: number[] = [];
  @Input() allowPaidCancel = false;
  /** cancel = annulation, facture = génération facture */
  @Input() selectionMode: 'cancel' | 'facture' = 'cancel';
  /** Onglet Consolidé : tickets déjà consolidés autorisés */
  @Input() allowConsolideTickets = false;
  @Output() rowsChange = new EventEmitter<MultiSelectTicketRow[]>();
  @Output() canProceedChange = new EventEmitter<boolean>();
  @Output() blockReasonChange = new EventEmitter<string | null>();
  /** Nombre de tickets retirés automatiquement (non facturables). */
  @Output() excludedCountChange = new EventEmitter<number>();

  rows: MultiSelectTicketRow[] = [];
  excludedCount = 0;
  blockReason: string | null = null;
  isLoading = false;
  /** Filtre de recherche (affichage uniquement : n'affecte pas les tickets facturés). */
  searchTerm = '';

  private loadedIdsKey = '';
  /** Dernier chargement brut (avant filtre homogénéité) — pour expliquer les exclusions. */
  private lastClassifiedRows: MultiSelectTicketRow[] = [];

  /** Lignes affichées après application du filtre de recherche. */
  get filteredRows(): MultiSelectTicketRow[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.rows;
    }
    return this.rows.filter((row) => {
      const haystack = [
        row.numero,
        row.clientName,
        row.entrepot,
        this.formatStatutLabel(row.statut, row.reglement),
        this.formatDate(row.date),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  /** Somme TTC algébrique des tickets retenus (avoirs négatifs inclus). */
  get selectionTotalTtc(): number {
    return this.rows.reduce((sum, r) => {
      const n = Number(r.total);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }

  /**
   * Total TTC pour la facture : hors acomptes / réservations avec paiement
   * (paiements, pas du chiffre d’affaires), avoirs et ventes inclus.
   */
  get selectionFactureTotalTtc(): number {
    return this.rows.reduce((sum, r) => {
      if (this.isAcompteSousType(r.sousType)) {
        return sum;
      }
      const n = Number(r.total);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }

  isAcompteSousType(sousType: string | null | undefined): boolean {
    const s = String(sousType || '')
      .trim()
      .toLowerCase();
    return (
      s === 'acompte' ||
      s === 'reservation_avec_paiement' ||
      s === 'reservation_avec_acompte'
    );
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  constructor(private readonly ticketService: LogicielCaisseTicketService) {}

  ngOnInit(): void {
    this.reloadIfIdsChanged(this.ticketIds);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticketIds']) {
      this.reloadIfIdsChanged(this.ticketIds);
    }
    if (changes['allowPaidCancel'] && this.rows.length) {
      this.emitState();
    }
    if (changes['allowConsolideTickets'] && this.rows.length) {
      this.emitState();
    }
    if (changes['selectionMode'] && this.rows.length) {
      this.emitState();
    }
  }

  removeRow(id: number): void {
    this.rows = this.rows.filter((r) => r.id !== id);
    this.emitState();
  }

  trackById(_: number, row: MultiSelectTicketRow): number {
    return row.id;
  }

  formatDate(iso: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleDateString('fr-FR');
  }

  formatStatutLabel(statut: string, reglement: string | null): string {
    const s = (statut || '').trim();
    if (s === 'termine') return 'Terminé';
    if (s === 'en_cours') return 'En cours';
    if (s === 'annulé' || s === 'annule') return 'Annulé';
    if (reglement === 'payé') return 'Payé';
    if (reglement === 'en_attente') return 'En attente';
    return s || '—';
  }

  rowClass(row: MultiSelectTicketRow): string {
    if (this.selectionMode === 'facture') {
      if (
        !row.canFacture ||
        row.kind === 'consolide_blocked' ||
        row.kind === 'particulier_blocked' ||
        row.kind === 'cloture_blocked'
      ) {
        return 'multi-ticket-row--admin';
      }
      return '';
    }
    if (row.requiresAdmin) {
      return 'multi-ticket-row--admin';
    }
    if (!row.canCancel) {
      return 'multi-ticket-row--blocked';
    }
    return '';
  }

  private reloadIfIdsChanged(ids: number[]): void {
    const key = this.idsKey(ids);
    if (key === this.loadedIdsKey) {
      return;
    }
    this.loadedIdsKey = key;
    this.loadTickets(ids);
  }

  private idsKey(ids: number[]): string {
    return [...(ids || [])]
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b)
      .join(',');
  }

  private loadTickets(rawIds: number[]): void {
    const ids = [
      ...new Set(
        (rawIds || []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
      ),
    ];
    if (ids.length === 0) {
      this.rows = [];
      this.lastClassifiedRows = [];
      this.isLoading = false;
      this.emitState();
      return;
    }

    this.isLoading = true;
    this.rows = [];
    this.lastClassifiedRows = [];
    this.emitState();

    forkJoin(
      ids.map((id) =>
        this.ticketService.getTicketDetail(id).pipe(
          map((data) => this.mapDetail(id, data)),
          catchError((err: unknown) => {
            const msg =
              err instanceof Error ? err.message : 'Chargement impossible';
            return of({
              id,
              numero: `#${id}`,
              date: '',
              entrepot: '—',
              entrepotId: null,
              entrepotCode: '001',
              statut: '',
              reglement: null,
              clientName: '—',
              clientId: null,
              clientType: 'particulier' as TicketClientType,
              isConsolide: false,
              isCloture: false,
              sousType: null,
              total: 0,
              subtotal: 0,
              paymentModes: [],
              kind: 'blocked' as MultiSelectTicketKind,
              requiresAdmin: false,
              canCancel: false,
              canFacture: false,
              loading: false,
              error: msg,
            });
          })
        )
      )
    )
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.emitState();
        })
      )
      .subscribe({
        next: (loaded) => {
          this.rows = loaded;
        },
        error: () => {
          this.rows = [];
          this.lastClassifiedRows = [];
        },
      });
  }

  private mapDetail(id: number, data: Record<string, unknown>): MultiSelectTicketRow {
    const statut = String(data['statut'] ?? '').trim();
    const reglement = data['reglement'] != null ? String(data['reglement']).trim() : null;
    const entrepot = String(data['entrepot_libelle'] ?? '—');
    const entrepotId =
      data['entrepot_id'] != null && Number(data['entrepot_id']) > 0
        ? Number(data['entrepot_id'])
        : null;
    const rawEntrepotCode = String(
      data['entrepot_code_entrepot'] ?? data['code_entrepot'] ?? ''
    ).trim();
    const entrepotCode =
      rawEntrepotCode ||
      (entrepotId != null && entrepotId > 0 ? String(entrepotId).padStart(3, '0') : '001');
    const rawClientId = data['client_id'];
    const clientId =
      rawClientId != null && Number(rawClientId) > 0 ? Number(rawClientId) : null;
    const code = String(data['code_document'] ?? `#${id}`);
    const isFactured =
      data['is_factured'] === true ||
      data['is_factured'] === 1 ||
      data['is_factured'] === '1' ||
      data['is_factured'] === 'true';
    const hasAvoireEmis =
      data['has_avoire_emis'] === true ||
      data['has_avoire_emis'] === 1 ||
      data['has_avoire_emis'] === '1' ||
      data['has_avoire_emis'] === 'true';
    const cancelClass = this.classifyCancel(statut, reglement, String(data['type'] ?? '').trim());
    const factureClass = this.classifyFacture(
      statut,
      reglement,
      isFactured,
      String(data['type'] ?? data['_documentType'] ?? '').trim(),
      hasAvoireEmis,
      this.detectClotureTicket(data, code)
    );

    const classification =
      this.selectionMode === 'facture' ? factureClass : cancelClass;

    const isConsolide =
      data['statut_consolidation'] === true ||
      data['statut_consolidation'] === 1 ||
      data['statut_consolidation'] === '1' ||
      data['statut_consolidation'] === 'true' ||
      data['consolide'] === true ||
      data['consolide'] === 1 ||
      data['consolide'] === '1' ||
      data['consolide'] === 'true' ||
      data['is_consolide'] === true ||
      data['is_consolide'] === 1;
    const clientDetail = data['client_detail'] as Record<string, unknown> | null;
    const rawClientType = String(
      clientDetail?.['type_client'] ?? data['client_type'] ?? 'particulier'
    )
      .trim()
      .toLowerCase();
    const clientType: TicketClientType =
      rawClientType === 'patente' ? 'patente' : 'particulier';
    const isCloture = this.detectClotureTicket(data, code);
    const sousRaw = data['sous_type'] ?? data['sousType'];
    const sousType =
      sousRaw != null && String(sousRaw).trim() !== '' ? String(sousRaw).trim() : null;
    let clientName = '—';
    if (clientDetail) {
      const prenom = String(clientDetail['prenom'] ?? '').trim();
      const nom = String(clientDetail['nom'] ?? '').trim();
      clientName = [prenom, nom].filter(Boolean).join(' ') || '—';
    } else if (data['client_phone']) {
      clientName = String(data['client_phone']).trim();
    }

    const rawModes = data['modes_paiement_par_type'] as Array<Record<string, unknown>> | undefined;
    const paymentModes = Array.isArray(rawModes)
      ? rawModes
          .map((m) => ({
            libelle: String(m['libelle'] ?? '').trim(),
            montant: Number(m['montant_total'] ?? m['montant'] ?? 0) || 0,
          }))
          .filter((m) => m.libelle && Number.isFinite(m.montant) && m.montant !== 0)
      : [];

    const subtotalRaw = Number(data['subtotal'] ?? 0);
    const subtotal = Number.isFinite(subtotalRaw) ? subtotalRaw : 0;
    const totalRaw = Number(data['total'] ?? 0);
    const total = Number.isFinite(totalRaw) ? totalRaw : 0;

    return {
      id,
      numero: code,
      date: String(data['date'] ?? ''),
      entrepot,
      entrepotId,
      entrepotCode,
      statut,
      reglement,
      clientName,
      clientId,
      clientType,
      isConsolide,
      isCloture,
      sousType,
      total,
      subtotal,
      paymentModes,
      kind: classification.kind,
      requiresAdmin: classification.requiresAdmin,
      canCancel: classification.canCancel,
      canFacture: classification.canFacture,
      loading: false,
      error: classification.error,
    };
  }

  private classifyCancel(
    statut: string,
    reglement: string | null,
    docType: string
  ): MultiSelectTicketClassification {
    if (docType === 'ticket_annule') {
      return { kind: 'already_cancelled', requiresAdmin: false, canCancel: false, canFacture: false };
    }
    if (docType === 'ticket_avoire') {
      return { kind: 'blocked', requiresAdmin: false, canCancel: false, canFacture: false };
    }
    const isPaid = this.isTicketReadyForFacture(statut, reglement);
    if (isPaid) {
      return { kind: 'paid', requiresAdmin: true, canCancel: true, canFacture: false };
    }
    if (statut === 'en_cours') {
      return { kind: 'available', requiresAdmin: false, canCancel: true, canFacture: false };
    }
    return { kind: 'blocked', requiresAdmin: false, canCancel: false, canFacture: false };
  }

  private detectClotureTicket(data: Record<string, unknown>, codeDocument: string): boolean {
    if (
      data['is_cloture'] === true ||
      data['is_cloture'] === 1 ||
      data['is_cloture'] === '1'
    ) {
      return true;
    }
    const code = String(codeDocument || data['code_document'] || '')
      .trim()
      .toLowerCase();
    if (code.startsWith('cloture-')) {
      return true;
    }
    const orig = String(data['document_original'] ?? '').trim();
    return /^clôture|^cloture/i.test(orig);
  }

  private classifyFacture(
    statut: string,
    reglement: string | null,
    isFactured: boolean,
    type: string,
    hasAvoireEmis = false,
    isCloture = false
  ): MultiSelectTicketClassification {
    if (isCloture) {
      return {
        kind: 'cloture_blocked',
        requiresAdmin: false,
        canCancel: false,
        canFacture: false,
        error: 'Ticket de clôture — non facturable',
      };
    }
    const docType = (type || '').trim();
    if (docType === 'ticket_annule') {
      return {
        kind: 'blocked',
        requiresAdmin: false,
        canCancel: false,
        canFacture: false,
        error: `Type ${docType} non facturable`,
      };
    }
    // ticket_vente déjà avoiré / ticket_avoire : facturables.
    if (isFactured) {
      return { kind: 'already_invoiced', requiresAdmin: false, canCancel: false, canFacture: false };
    }
    if (!docType) {
      return {
        kind: 'blocked',
        requiresAdmin: false,
        canCancel: false,
        canFacture: false,
        error: 'type ticket_vente manquant',
      };
    }
    if (docType === 'ticket_en_traitement') {
      return {
        kind: 'blocked',
        requiresAdmin: false,
        canCancel: false,
        canFacture: false,
        error: 'Seul un ticket de vente (ticket_vente) peut être facturé',
      };
    }
    if (docType !== 'ticket_vente' && docType !== 'ticket_avoire') {
      return {
        kind: 'blocked',
        requiresAdmin: false,
        canCancel: false,
        canFacture: false,
        error: `Type ${docType} non facturable`,
      };
    }
    if (docType === 'ticket_avoire' || hasAvoireEmis) {
      return { kind: 'ready', requiresAdmin: false, canCancel: false, canFacture: true };
    }
    if (!this.isTicketReadyForFacture(statut, reglement)) {
      return {
        kind: 'not_ready',
        requiresAdmin: false,
        canCancel: false,
        canFacture: false,
        error: 'Ticket non terminé / non payé',
      };
    }
    return { kind: 'ready', requiresAdmin: false, canCancel: false, canFacture: true };
  }

  /** Aligné sur la liste POS (payé / paye, termine sans accent). */
  private isTicketReadyForFacture(statut: string, reglement: string | null): boolean {
    const norm = (v: string) =>
      v
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const s = norm(statut || '');
    const r = reglement != null ? norm(reglement) : '';
    return s === 'termine' || r === 'paye';
  }

  private applyFactureRowRules(): void {
    const hasPatente = this.rows.some((r) => r.clientType === 'patente' && r.canFacture);
    const hasParticulier = this.rows.some((r) => r.clientType === 'particulier' && r.canFacture);
    const mixedClientTypes = hasPatente && hasParticulier;

    this.rows = this.rows.map((row) => {
      let kind = row.kind;
      let canFacture = row.canFacture;
      let error = row.error;

      if (row.canFacture && row.isConsolide && !this.allowConsolideTickets) {
        kind = 'consolide_blocked';
        canFacture = false;
        error = 'Ticket déjà consolidé';
      } else if (row.isCloture || row.kind === 'cloture_blocked') {
        kind = 'cloture_blocked';
        canFacture = false;
        error = 'Ticket de clôture — non facturable';
      } else if (row.canFacture && mixedClientTypes && row.clientType === 'particulier') {
        kind = 'particulier_blocked';
        canFacture = false;
        error = 'Client particulier — incompatible avec une facture entreprise (patenté)';
      }

      return { ...row, kind, canFacture, error };
    });
  }

  /** Clé entrepôt pour regrouper (id prioritaire, sinon libellé). */
  private entrepotGroupKey(row: MultiSelectTicketRow): string {
    if (row.entrepotId != null && row.entrepotId > 0) {
      return `id:${row.entrepotId}`;
    }
    const label = (row.entrepot || '').trim().toLowerCase();
    return label ? `label:${label}` : 'unknown';
  }

  /**
   * Garde le plus grand sous-ensemble homogène (même entrepôt, même client).
   * Les tickets sans client peuvent rejoindre n’importe quel groupe client.
   */
  private applyFactureHomogeneityFilter(rows: MultiSelectTicketRow[]): MultiSelectTicketRow[] {
    if (rows.length <= 1) {
      return rows;
    }

    const byEntrepot = new Map<string, MultiSelectTicketRow[]>();
    for (const row of rows) {
      const key = this.entrepotGroupKey(row);
      const list = byEntrepot.get(key) ?? [];
      list.push(row);
      byEntrepot.set(key, list);
    }

    // Important : partir de [] pour vraiment retenir le plus grand groupe compatible
    // (sinon on conserve toute la sélection même mélangeée entrepôts / clients).
    let bestSubset: MultiSelectTicketRow[] = [];

    for (const entrepotRows of byEntrepot.values()) {
      const clientCompatibleSubsets: MultiSelectTicketRow[][] = [];

      const withoutClient = entrepotRows.filter((r) => r.clientId == null || r.clientId <= 0);
      if (withoutClient.length) {
        clientCompatibleSubsets.push(withoutClient);
      }

      const clientIds = [
        ...new Set(
          entrepotRows
            .map((r) => r.clientId)
            .filter((id): id is number => id != null && id > 0)
        ),
      ];
      for (const clientId of clientIds) {
        clientCompatibleSubsets.push(
          entrepotRows.filter((r) => !r.clientId || r.clientId <= 0 || r.clientId === clientId)
        );
      }

      for (const subset of clientCompatibleSubsets) {
        if (subset.length > bestSubset.length) {
          bestSubset = subset;
        }
      }
    }

    return bestSubset.length ? bestSubset : rows.slice(0, 1);
  }

  private buildFactureExclusionReason(classified: MultiSelectTicketRow[]): string {
    const counts = new Map<string, number>();
    const bump = (label: string) => counts.set(label, (counts.get(label) || 0) + 1);

    for (const row of classified) {
      if (row.kind === 'cloture_blocked') {
        bump('clôture');
      } else if (row.kind === 'already_invoiced') {
        bump('déjà facturés');
      } else if (row.kind === 'consolide_blocked') {
        bump('déjà consolidés');
      } else if (row.kind === 'not_ready') {
        bump('non terminés / non payés');
      } else if (row.kind === 'particulier_blocked') {
        bump('clients particuliers incompatibles');
      } else if (row.error && !row.statut && !row.date) {
        bump('chargement impossible');
      } else if (!row.canFacture) {
        bump(row.error || 'non facturables');
      }
    }

    if (!counts.size) {
      return 'Aucun ticket facturable et compatible (même entrepôt, même client) dans la sélection.';
    }

    const detail = [...counts.entries()]
      .map(([label, n]) => `${n} ${label}`)
      .join(', ');
    return `Aucun ticket retenu : ${detail}. Sélectionnez des tickets tickés payés, même entrepôt et même client.`;
  }

  private emitState(): void {
    let canProceed = false;
    let blockReason: string | null = null;

    if (this.isLoading) {
      this.excludedCount = 0;
      this.excludedCountChange.emit(0);
      this.rowsChange.emit(this.rows);
      this.blockReason = null;
      this.canProceedChange.emit(false);
      this.blockReasonChange.emit(null);
      return;
    }

    if (this.selectionMode === 'facture' && this.rows.length) {
      this.applyFactureRowRules();
      this.lastClassifiedRows = [...this.rows];
      const initialCount = this.rows.length;
      let facturable = this.rows.filter((r) => r.canFacture && r.kind === 'ready');
      facturable = this.applyFactureHomogeneityFilter(facturable);
      this.excludedCount = initialCount - facturable.length;

      if (facturable.length > 0) {
        this.rows = facturable;
        canProceed = true;
        blockReason = null;
      } else {
        // Garder la liste classifiée pour afficher les motifs (évite écran vide opaque).
        this.rows = this.lastClassifiedRows;
        canProceed = false;
        blockReason = this.buildFactureExclusionReason(this.lastClassifiedRows);
      }
      this.excludedCountChange.emit(this.excludedCount);
    } else {
      this.excludedCount = 0;
      this.excludedCountChange.emit(0);
      if (this.rows.length === 0) {
        blockReason = 'Aucun ticket dans la sélection.';
      } else if (this.rows.some((r) => r.error && !r.statut && !r.date)) {
        blockReason = 'Certains tickets n\'ont pas pu être chargés.';
      } else {
        canProceed =
          this.rows.length > 0 &&
          this.rows.every((r) => !r.loading && !r.error) &&
          this.rows.every(
            (r) => r.canCancel && (!r.requiresAdmin || this.allowPaidCancel)
          );
        if (!canProceed && this.rows.length > 0) {
          blockReason = 'Retirez les tickets bloqués ou non autorisés pour continuer.';
        }
      }
    }

    this.rowsChange.emit(this.rows);
    this.blockReason = blockReason;
    this.canProceedChange.emit(canProceed);
    this.blockReasonChange.emit(blockReason);
  }
}
